import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { SMBeamModel } from '../types'
import { ResizablePanels } from '@structura/ui'
import { useResizablePanels } from '@structura/ui'
import ResponseControlPanel from './response/ResponseControlPanel'
import ResponseVisualizationPanel from './response/ResponseVisualizationPanel'
import { ResponseDiagramType, isSlsResponseDiagram } from './response/types'
import { resolveDiagramSource, isDiagramEvaluated, DiagramSourceResolution } from './response/combinationSource'
import { ResponseDataResponse } from '../utils/analysisApi'
import { useTranslation } from '@structura/i18n'

interface ResponseTabProps {
  model: SMBeamModel
  result: any
  selectedDiagram: ResponseDiagramType
  setSelectedDiagram: React.Dispatch<React.SetStateAction<ResponseDiagramType>>
  showValues: boolean
  setShowValues: React.Dispatch<React.SetStateAction<boolean>>
  showMaxMin: boolean
  setShowMaxMin: React.Dispatch<React.SetStateAction<boolean>>
  valueDecimals: 2 | 4
  setValueDecimals: React.Dispatch<React.SetStateAction<2 | 4>>
  diagramScale: number
  setDiagramScale: React.Dispatch<React.SetStateAction<number>>
  valueDensity: number
  setValueDensity: React.Dispatch<React.SetStateAction<number>>
  valueFontSize: number
  setValueFontSize: React.Dispatch<React.SetStateAction<number>>
  getSessionHeaders: () => Record<string, string>
  setResult: React.Dispatch<React.SetStateAction<any>>
  onCalculateResponse: () => Promise<void>
  calculatingResponse: boolean
  currentMaterial: string
}

const ResponseTab: React.FC<ResponseTabProps> = ({ 
  model,
  result,
  selectedDiagram,
  setSelectedDiagram,
  showValues,
  setShowValues,
  showMaxMin,
  setShowMaxMin,
  valueDecimals,
  setValueDecimals,
  diagramScale,
  setDiagramScale,
  valueDensity,
  setValueDensity,
  valueFontSize,
  setValueFontSize,
  getSessionHeaders: _getSessionHeaders,
  onCalculateResponse,
  calculatingResponse,
  currentMaterial
}) => {
  const { t } = useTranslation()
  const { leftWidth, rightWidth, handleMouseDown, isDragging, containerRef } = useResizablePanels({
    defaultLeftWidth: 30,
    minLeftWidth: 3,
    maxLeftWidth: 97,
    storageKey: 'beamDesigner_responseTab_leftWidth'
  })

  // Load combination selection for URs
  const [selectedUlsCombination, setSelectedUlsCombination] = useState<string>('')
  const [selectedSlsCombination, setSelectedSlsCombination] = useState<string>('')
  const [activeCombinationId, setActiveCombinationId] = useState<string>('')
  const [activeCombinationLabel, setActiveCombinationLabel] = useState<string>('')
  const [showUlsDetails, setShowUlsDetails] = useState<boolean>(false)
  const [showSlsDetails, setShowSlsDetails] = useState<boolean>(false)

  // Build combination options from response response data (all limit states)
  const combinationMetadata = useMemo(() => {
    const rd: any = result?.ResponseData
    if (!rd) return [] as any[]
    const responses: any[] = rd.CombinationResponses || rd.combinationResponses || []
    return responses
  }, [result])

  const { ulsCombinations, slsCombinations } = useMemo(() => {
    const uls: Array<{ id: string; label: string }> = []
    const sls: Array<{ id: string; label: string }> = []

    for (const combo of combinationMetadata) {
      const rawLimitState = (combo.limitState || combo.LimitState || '') as string
      const limitState = rawLimitState || ''
      const combId = combo.combinationId || combo.CombinationId || ''
      const leading = combo.leadingCategory || combo.LeadingCategory || 'None'

      if (!combId || !limitState) {
        continue
      }

      const baseLeading =
        leading === 'None'
          ? t('beam.results.noVariableLeading')
          : t('beam.response.leadingNoColon', { category: leading })

      const label = `${limitState} – ${baseLeading}`

      if (limitState === 'ULS') {
        uls.push({ id: combId, label })
      } else if (limitState.startsWith('SLS')) {
        sls.push({ id: combId, label })
      }
    }

    return { ulsCombinations: uls, slsCombinations: sls }
  }, [combinationMetadata, t])

  const findCombinationLabel = useCallback((id: string) => {
    if (!id) {
      return ''
    }
    const fromUls = ulsCombinations.find((combo) => combo.id === id)
    if (fromUls) {
      return fromUls.label
    }
    const fromSls = slsCombinations.find((combo) => combo.id === id)
    return fromSls?.label || ''
  }, [ulsCombinations, slsCombinations])

  const updateActiveCombination = useCallback((id: string) => {
    if (!id) {
      return
    }
    setActiveCombinationId(id)
    setActiveCombinationLabel(findCombinationLabel(id))
  }, [findCombinationLabel])

  useEffect(() => {
    if (ulsCombinations.length > 0 && !ulsCombinations.some(combo => combo.id === selectedUlsCombination)) {
      const nextId = ulsCombinations[0].id
      setSelectedUlsCombination(nextId)
      if (!activeCombinationId) {
        updateActiveCombination(nextId)
      }
    }
  }, [ulsCombinations, selectedUlsCombination, activeCombinationId, updateActiveCombination])

  useEffect(() => {
    if (slsCombinations.length > 0 && !slsCombinations.some(combo => combo.id === selectedSlsCombination)) {
      const nextId = slsCombinations[0].id
      setSelectedSlsCombination(nextId)
    }
  }, [slsCombinations, selectedSlsCombination])

  const handleSelectUlsCombination = useCallback((value: string) => {
    setSelectedUlsCombination(value)
    updateActiveCombination(value)
  }, [updateActiveCombination])

  const handleSelectSlsCombination = useCallback((value: string) => {
    setSelectedSlsCombination(value)
    updateActiveCombination(value)
  }, [updateActiveCombination])

  const handleToggleUlsDetails = useCallback(() => {
    setShowUlsDetails(prev => !prev)
    const targetId = selectedUlsCombination || (ulsCombinations.length > 0 ? ulsCombinations[0].id : '')
    if (targetId) {
      updateActiveCombination(targetId)
    }
  }, [selectedUlsCombination, ulsCombinations, updateActiveCombination])

  const handleToggleSlsDetails = useCallback(() => {
    setShowSlsDetails(prev => !prev)
    const targetId = selectedSlsCombination || (slsCombinations.length > 0 ? slsCombinations[0].id : '')
    if (targetId) {
      updateActiveCombination(targetId)
    }
  }, [selectedSlsCombination, slsCombinations, updateActiveCombination])

  // Persist UI settings per response diagram
  useEffect(() => {
    try {
      const key = `responseDiagram_ui_${selectedDiagram}`
      const payload = { scale: diagramScale, valueDensity, valueFontSize, showValues, showMaxMin, valueDecimals }
      localStorage.setItem(key, JSON.stringify(payload))
    } catch { /* localStorage unavailable */ }
  }, [selectedDiagram, diagramScale, valueDensity, valueFontSize, showValues, showMaxMin, valueDecimals])

  useEffect(() => {
    try {
      const key = `responseDiagram_ui_${selectedDiagram}`
      const saved = localStorage.getItem(key)
      if (saved) {
        const parsed = JSON.parse(saved)
        if (typeof parsed.scale === 'number') setDiagramScale(parsed.scale)
        if (typeof parsed.valueDensity === 'number') setValueDensity(parsed.valueDensity)
        if (typeof parsed.valueFontSize === 'number') setValueFontSize(parsed.valueFontSize)
        if (typeof parsed.showValues === 'boolean') setShowValues(parsed.showValues)
        if (typeof parsed.showMaxMin === 'boolean') setShowMaxMin(parsed.showMaxMin)
        if (parsed.valueDecimals === 2 || parsed.valueDecimals === 4) setValueDecimals(parsed.valueDecimals)
      }
    } catch { /* localStorage unavailable */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDiagram])

  // Extract responseData before using it in useMemos
  const responseData = result?.ResponseData
  const hasResponseData = Boolean(responseData)

  // Get display name for current diagram
  const combinationDataMap = useMemo(() => {
    const map = new Map<string, any>()
    const combinationResponses = responseData?.CombinationResponses || responseData?.combinationResponses || []
    for (const combResp of combinationResponses) {
      const combId = combResp.combinationId || combResp.CombinationId
      const data = (combResp.data || combResp.Data) as ResponseDataResponse | undefined
      if (combId && data) {
        map.set(combId, data)
      }
    }
    return map
  }, [responseData])

  const responseDiagram = useMemo(() => {
    // Determine which data source to use based on diagram type and selected combination
    const isUlsDiagram = selectedDiagram.startsWith('ur') && !selectedDiagram.startsWith('urSls')
    const isSlsDiagram = isSlsResponseDiagram(selectedDiagram)  // covers the UR diagrams AND the w_inst/w_fin curves
    const isStrainStressDiagram = selectedDiagram.startsWith('epsilon') || selectedDiagram.startsWith('sigma') ||
                                   selectedDiagram.startsWith('gamma') || selectedDiagram.startsWith('tau')
    let diagramCombinationId = ''
    let source: DiagramSourceResolution | null = null

    if (isUlsDiagram) {
      // ULS URs always follow the ULS combination selection
      diagramCombinationId = selectedUlsCombination || (ulsCombinations.length > 0 ? ulsCombinations[0].id : '')
    } else if (isSlsDiagram) {
      // SLS URs and the w_inst/w_fin curves follow the SLS combination selection — but each SLS
      // check is evaluated only on its code-mandated combination (backend marks this with the
      // explicit Evaluated flag and zero-fills elsewhere). The resolver honors the selection
      // whenever that combination evaluated the check (a genuine zero stays displayed) and
      // otherwise routes to the combination that did; the resolved source is shown in the UI.
      const preferredSlsId = selectedSlsCombination || (slsCombinations.length > 0 ? slsCombinations[0].id : '')
      source = resolveDiagramSource(preferredSlsId, slsCombinations.map(combo => combo.id), combinationDataMap, selectedDiagram)
      diagramCombinationId = source.combinationId
    } else if (isStrainStressDiagram) {
      // Strains and stresses follow the currently active combination label (ULS or SLS)
      diagramCombinationId =
        activeCombinationId ||
        selectedUlsCombination ||
        selectedSlsCombination ||
        (ulsCombinations.length > 0 ? ulsCombinations[0].id : '') ||
        (slsCombinations.length > 0 ? slsCombinations[0].id : '') ||
        ''
    } else {
      // Fallback for any other diagram types (should not normally be hit)
      diagramCombinationId =
        activeCombinationId ||
        selectedUlsCombination ||
        selectedSlsCombination ||
        (ulsCombinations.length > 0 ? ulsCombinations[0].id : '') ||
        (slsCombinations.length > 0 ? slsCombinations[0].id : '') ||
        ''
    }

    const emptyDiagram = { success: false, data: [], count: 0, maxValue: 0, unit: '' }

    if (diagramCombinationId) {
      const combinationData = combinationDataMap.get(diagramCombinationId)
      if (combinationData) {
        return { data: combinationData[selectedDiagram] || emptyDiagram, source }
      }
    }

    if (isStrainStressDiagram) {
      const diagrams = result?.ResponseData || {}
      return { data: diagrams[selectedDiagram] || emptyDiagram, source }
    }

    return { data: emptyDiagram, source }
  }, [
    result,
    responseData,
    selectedDiagram,
    selectedUlsCombination,
    selectedSlsCombination,
    activeCombinationId,
    ulsCombinations,
    slsCombinations,
    combinationDataMap
  ])

  const responseDiagramData = responseDiagram.data
  const diagramSource = responseDiagram.source
  // Provenance is first-class: when the diagram comes from a different combination than the
  // dropdown selection, name the actual source next to the chart; when no combination evaluated
  // the check at all, say so instead of showing a silent zero curve.
  const diagramSourceLabel = diagramSource && diagramSource.redirected ? findCombinationLabel(diagramSource.combinationId) : ''
  const diagramNotEvaluated = diagramSource !== null && !diagramSource.evaluated

  // The concrete (reinforcement) and timber stress cards are availability-driven: they appear only
  // when some SLS combination in the current response actually evaluated the shared
  // urSlsSteelStress check (the timber Stress_Timber_Quasi limit is deliberately not defaulted).
  const steelStressEvaluated: boolean = useMemo(() => {
    return slsCombinations.some(combo => isDiagramEvaluated(combinationDataMap.get(combo.id), 'urSlsSteelStress'))
  }, [slsCombinations, combinationDataMap])

  const activeUlsCombinationDetails = useMemo(() => {
    if (!selectedUlsCombination || !responseData) return null
    const combos: any[] = (responseData as any).CombinationResponses || (responseData as any).combinationResponses || []
    return combos.find(c => (c.combinationId || c.CombinationId) === selectedUlsCombination) || null
  }, [responseData, selectedUlsCombination])

  const activeSlsCombinationDetails = useMemo(() => {
    if (!selectedSlsCombination || !responseData) return null
    const combos: any[] = (responseData as any).CombinationResponses || (responseData as any).combinationResponses || []
    return combos.find(c => (c.combinationId || c.CombinationId) === selectedSlsCombination) || null
  }, [responseData, selectedSlsCombination])

  return (
    <>
      <ResizablePanels
        leftPanel={(
          <ResponseControlPanel
            result={result}
            onCalculateResponse={onCalculateResponse}
            calculatingResponse={calculatingResponse}
            selectedDiagram={selectedDiagram}
            setSelectedDiagram={setSelectedDiagram}
            showValues={showValues}
            setShowValues={setShowValues}
            showMaxMin={showMaxMin}
            setShowMaxMin={setShowMaxMin}
            valueDecimals={valueDecimals}
            setValueDecimals={setValueDecimals}
            diagramScale={diagramScale}
            setDiagramScale={setDiagramScale}
            valueDensity={valueDensity}
            setValueDensity={setValueDensity}
            valueFontSize={valueFontSize}
            setValueFontSize={setValueFontSize}
            selectedUlsCombination={selectedUlsCombination}
            onSelectUlsCombination={handleSelectUlsCombination}
            selectedSlsCombination={selectedSlsCombination}
            onSelectSlsCombination={handleSelectSlsCombination}
            ulsCombinations={ulsCombinations}
            slsCombinations={slsCombinations}
            activeCombinationLabel={activeCombinationLabel}
            onToggleUlsDetails={handleToggleUlsDetails}
            onToggleSlsDetails={handleToggleSlsDetails}
            ulsCombinationDetails={showUlsDetails ? activeUlsCombinationDetails : null}
            slsCombinationDetails={showSlsDetails ? activeSlsCombinationDetails : null}
            steelStressEvaluated={steelStressEvaluated}
            currentMaterial={currentMaterial}
          />
        )}
        rightPanel={(
          <ResponseVisualizationPanel
            model={model}
            currentMaterial={currentMaterial}
            selectedDiagram={selectedDiagram}
            diagramScale={diagramScale}
            showValues={showValues}
            showMaxMin={showMaxMin}
            valueDecimals={valueDecimals}
            valueDensity={valueDensity}
            valueFontSize={valueFontSize}
            diagramData={responseDiagramData}
            diagramAvailable={hasResponseData}
            diagramSourceLabel={diagramSourceLabel}
            diagramNotEvaluated={diagramNotEvaluated}
            globalChecks={responseData?.GlobalChecks || (responseData as any)?.globalChecks}
          />
        )}
        leftWidth={leftWidth}
        rightWidth={rightWidth}
        onMouseDown={handleMouseDown}
        isDragging={isDragging}
        containerRef={containerRef}
      />
    </>
  )
}

export default ResponseTab
